import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Button, Alert } from 'react-bootstrap';
import { SharedProductItemDto } from '../../requests/useGetSharedProductConnections';
import { KaSelect, KaSelectOption } from '../Common/KaSelect';
import { SingleValue } from 'react-select';
import { calculateSimilarity } from '../../Utilities';

interface ProductSelectionModalProps {
  onClose: () => void;
  onSelect: (productId: string) => void;
  products: SharedProductItemDto[];
  siteProducts: SharedProductItemDto[];
  productName: string;
  sourceBusinessName: string;
  existingMappings: { [productId: string]: string[] };
}

export const ProductSelectionModal = (props: ProductSelectionModalProps) => {
  const [selectedOption, setSelectedOption] = useState<SingleValue<KaSelectOption>>(null);


  const productOptions: KaSelectOption[] = props.products.map(product => ({
    value: product.id,
    label: product.name
  }));

  const sortedOptions = productOptions.sort((a, b) => a.label.localeCompare(b.label));

  const getExistingMapping = useCallback((sourceProductId: string): string | undefined => {
    const existingMapping = Object.entries(props.existingMappings).find(m => m[1].includes(sourceProductId));

    return props.siteProducts.find(p => p.id === existingMapping?.[0])?.name;
  }, [props.existingMappings, props.siteProducts]);

  useEffect(() => {
    if (selectedOption) return;

    let bestMatch: KaSelectOption | null = null;
    let bestScore = 0;

    sortedOptions
      .filter(option => !getExistingMapping(option.value))
      .forEach(option => {
        const score = calculateSimilarity(props.productName, option.label);
        if (score > bestScore && score >= 0.5) {
          bestScore = score;
          bestMatch = option;
        }
      });

    setSelectedOption(bestMatch);
  }, [props.productName, sortedOptions, selectedOption, getExistingMapping]);

  const handleSubmit = () => {
    if (selectedOption?.value && !getExistingMapping(selectedOption.value)) {
      props.onSelect(selectedOption.value);
      setSelectedOption(null);
    }
  };

  const handleClose = () => {
    setSelectedOption(null);
    props.onClose();
  };

  const isSelectedAlreadyMapped = !!(selectedOption && getExistingMapping(selectedOption.value));

  const errorMessage = selectedOption && getExistingMapping(selectedOption.value)
    ? `"${selectedOption.label}" is already mapped to "${getExistingMapping(selectedOption.value)}".`
    : '';

  return <Modal show={true} onHide={handleClose} size="lg">
    <Modal.Header closeButton>
      <Modal.Title>Select Source Product</Modal.Title>
    </Modal.Header>
    <Modal.Body>
      <div className="mb-4">
        <p className="text-muted">
          Mapping <strong>{props.productName}</strong> to a product from <strong>{props.sourceBusinessName}</strong>
        </p>
      </div>

      <div className="mb-3">
        <KaSelect
          options={sortedOptions}
          value={selectedOption}
          onChange={(option) => setSelectedOption(option as SingleValue<KaSelectOption>)}
          placeholder="Choose a source product..."
          isClearable={false}
          isSearchable={true}
        />
      </div>

      {errorMessage && (
        <Alert variant="danger">
          {errorMessage}
        </Alert>
      )}

      {props.products.length === 0 && (
        <Alert variant="info">
          No products available from this source.
        </Alert>
      )}
    </Modal.Body>
    <Modal.Footer>
      <Button variant="secondary" onClick={handleClose}>
        Cancel
      </Button>
      <Button
        variant="primary"
        onClick={handleSubmit}
        disabled={!selectedOption || isSelectedAlreadyMapped}
      >
        Add Mapping
      </Button>
    </Modal.Footer>
  </Modal>
};